import type { FinTaxCalendarSummary, FinTaxCalendarItemRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { IconPlus } from '@shared/ui/icons';
import {
  formatDateRu,
  formatMillions,
  formatMoney,
  TAX_TYPE_LABELS,
} from '../financierFormat';
import {
  Section,
  SectionHeader,
  SectionTitle,
  Subtitle,
  StatGrid,
  MetricBox,
  MetricLabel,
  MetricValue,
  MetricNote,
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  PeriodText,
  DueWarningText,
  BoldText,
  DimSubText,
  ReservedPurpleTd,
  PaidGreenTd,
} from './styled';
import { TAX_STATUS_LABELS } from './types';

interface TaxBalanceSectionProps {
  taxData: FinTaxCalendarSummary | undefined;
  isTaxLoading: boolean;
  isTaxError: boolean;
  refetchTax: () => void;
  onOpenTaxModal: () => void;
  canWrite: boolean;
}

export function TaxBalanceSection({
  taxData,
  isTaxLoading,
  isTaxError,
  refetchTax,
  onOpenTaxModal,
  canWrite,
}: TaxBalanceSectionProps) {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>Онлайн-баланс чистого НДС и налоговый резерв ЕНС</SectionTitle>
          <Subtitle>
            Сопоставление начисленного НДС по подписанным Заказчиками КС-3 с входящим вычетом по счетам-фактурам МТО
          </Subtitle>
        </div>
        {canWrite && (
          <Button $variant="primary" $size="sm" onClick={onOpenTaxModal}>
            <IconPlus size={14} />
            Добавить налоговое обязательство
          </Button>
        )}
      </SectionHeader>

      {taxData && (
        <StatGrid>
          <MetricBox>
            <MetricLabel>Исходящий НДС (с реализации)</MetricLabel>
            <MetricValue>{formatMillions(taxData.vat_outgoing_signed)}</MetricValue>
            <MetricNote>По подписанным актам КС-3</MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Входящий НДС (к вычету)</MetricLabel>
            <MetricValue $color="#10b981">{formatMillions(taxData.vat_incoming_deductible)}</MetricValue>
            <MetricNote>Счета-фактуры и УПД поставщиков</MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Чистый НДС к уплате (за квартал)</MetricLabel>
            <MetricValue $color={taxData.net_vat_payable > 0 ? '#ef4444' : '#10b981'}>
              {formatMillions(taxData.net_vat_payable)}
            </MetricValue>
            <MetricNote>Ежемесячный платёж (1/3 к 28-му числу): {formatMillions(taxData.net_vat_payable / 3)}</MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Баланс налогового резерва ЕНС</MetricLabel>
            <MetricValue $color="#8b5cf6">{formatMillions(taxData.tax_reserve_balance)}</MetricValue>
            <MetricNote>
              Покрытие обязательств: <b>{taxData.reserve_coverage_pct.toFixed(0)}%</b>
            </MetricNote>
          </MetricBox>
        </StatGrid>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Период / Срок уплаты</th>
              <th>Вид налога</th>
              <th>Обязательство / Юрлицо</th>
              <RightTh>Начислено, ₽</RightTh>
              <RightTh>Зарезервировано, ₽</RightTh>
              <RightTh>Уплачено, ₽</RightTh>
              <th>Статус ЕНС</th>
            </tr>
          </thead>
          <tbody>
            {isTaxLoading ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Загрузка налогового календаря…
                </TableNoticeTd>
              </tr>
            ) : isTaxError ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Не удалось загрузить налоговый календарь ЕНС.{' '}
                  <Button $variant="secondary" $size="sm" onClick={refetchTax}>
                    Повторить
                  </Button>
                </TableNoticeTd>
              </tr>
            ) : !taxData?.items || taxData.items.length === 0 ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Обязательства не найдены
                </TableNoticeTd>
              </tr>
            ) : (
              taxData.items.map((t: FinTaxCalendarItemRow) => {
                const conf = TAX_STATUS_LABELS[t.status] ?? {
                  label: t.status,
                  tone: 'neutral',
                };
                return (
                  <tr key={t.id}>
                    <td>
                      <PeriodText>{t.tax_period}</PeriodText>
                      <DueWarningText>
                        Срок: {formatDateRu(t.due_date)}
                      </DueWarningText>
                    </td>
                    <td>
                      <BoldText>
                        {TAX_TYPE_LABELS[t.tax_type] ?? t.tax_type}
                      </BoldText>
                    </td>
                    <td>
                      <BoldText>{t.title}</BoldText>
                      <DimSubText>{t.entity_name}</DimSubText>
                    </td>
                    <RightTd>
                      <PeriodText>{formatMoney(t.accrued_amount)}</PeriodText>
                    </RightTd>
                    <ReservedPurpleTd>
                      {formatMoney(t.reserved_amount)}
                    </ReservedPurpleTd>
                    <PaidGreenTd>
                      {formatMoney(t.paid_amount)}
                    </PaidGreenTd>
                    <td>
                      <Badge $tone={conf.tone}>{conf.label}</Badge>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
